import React, { useState, useEffect } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { PlusOutlined, MinusOutlined } from "us.icons";
import { Formik } from "formik";
import { withRouter } from "react-router-dom";
import Common from "us.common";
import "./DebtorInformation.scss";
import AddressHistory from "./AddressHistory";
import EntityHistory from "./DebtorHistory";
import _ from "lodash";
import { debtor } from "us.collection.debtor/actions";
import { ValidationSchema } from "../Validation";
import { entity } from "us.common/actions";
import {
	ISummaryAmount,
	IBase,
} from "us.collection.debtor/components/DebtorInfo/Interfaces";
import PostalArea from "us.common/containers/PostalArea/PostalArea";
import { DateFormats } from "us.collection.debtor/components/DebtorInfo/Constants";
import { formInitialValue } from "us.collection.debtor/components/DebtorInfo/Functions";
import { IRootState } from "us.collection/interfaces";
import { validatePersonNo } from "us.collection.debtor/components/DebtorInfo/Functions";
import { appInsights } from "us.helper";
import {
	isSubCaseTransferredToCase,
	getPhoneNumbers,
} from "us.common/functions";
import { DEFAULT_COUNTRY_CODE } from "us.common/constants";
import { AddressInfo, BasicInfo, CreditorInfo, OtherInfo } from "./components";

const { $Form, $Button, $Divider, $Collapse, $Drawer, $AmountLabel } =
	Common.Components;

const General: React.FC<IBase> = (props) => {
	const { t } = useTranslation();
	const [creditorTableData, setCreditorTableData] = useState<Array<any>>([]);
	const [addressUnknownConfirmVisible, setAddressUnknownConfirmVisible] =
		useState<boolean>(false);
	const [filledData, setFIlledData] = useState<any>({});
	const [AddressHistoryVisible, setAddressHistoryVisible] =
		useState<boolean>(false);
	const [DebtorHistoryVisible, setDebtorHistoryVisible] =
		useState<boolean>(false);
	const [expandKey, setExpandKey] = useState<Array<string>>([]);
	const [summaryData, setSummaryData] = useState<Array<ISummaryAmount>>([
		{
			numberOfCases: 0,
			reminder: "0.00",
			collection: "0.00",
			court: "0.00",
			surviellance: "0.00",
			balance: "0.00",
		},
	]);
	const {
		currentDateFormat,
		isFetching,
		debtorInformation,
		isFetchingCreditorInfo,
		getCreditorInfo,
		creditorInfo,
		getOtherInfo,
		isFetchingOtherInformation,
		otherInfo,
		save,
		getEntity,
		drawerName,
		isSaving,
		searchPostalcode,
		searchMunicipility,
		postalcode,
		metaData,
	} = props;
	const { debtorId, arId } = metaData.data ?? {};

	useEffect(() => {
		if (metaData.data) {
			getEntity(debtorId);
			getOtherInfo(arId);
		}
	}, [metaData]);

	useEffect(() => {
		if (creditorInfo.length !== 0) {
			const temData: any = [];
			const summaryTem: any = [];
			try {
				Object.values(_.groupBy(creditorInfo, "pid")).map((opr: any) => {
					if (opr.length > 1) {
						temData.push({
							pid: opr[0].pid,
							creditorName: opr[0].creditorName,
							numberOfCases: opr[0].numberOfCases,
							balance: _.sum(
								opr.map((o: any) => o.balance).map(Number),
							).toFixed(2),
							collectionFee: _.sum(
								opr.map((o: any) => o.collectionFee).map(Number),
							).toFixed(2),
							reminderFee: _.sum(
								opr.map((o: any) => o.reminderFee).map(Number),
							).toFixed(2),
							courtFee: _.sum(
								opr.map((o: any) => o.courtFee).map(Number),
							).toFixed(2),
							surveillanceFee: _.sum(
								opr.map((o: any) => o.surveillanceFee).map(Number),
							).toFixed(2),
						});
					} else {
						temData.push({
							pid: opr[0].pid,
							creditorName: opr[0].creditorName,
							numberOfCases: opr[0].numberOfCases,
							balance: _.sum(
								opr.map((o: any) => o.balance).map(Number),
							).toFixed(2),
							collectionFee: _.sum(
								opr.map((o: any) => o.collectionFee).map(Number),
							).toFixed(2),
							reminderFee: _.sum(
								opr.map((o: any) => o.reminderFee).map(Number),
							).toFixed(2),
							courtFee: _.sum(
								opr.map((o: any) => o.courtFee).map(Number),
							).toFixed(2),
							surveillanceFee: _.sum(
								opr.map((o: any) => o.surveillanceFee).map(Number),
							).toFixed(2),
						});
					}
				});

				summaryTem.push({
					numberOfCases: _.sum(
						temData.map((o: any) => o.numberOfCases).map(Number),
					),
					reminder: _.sum(
						temData.map((o: any) => o.reminderFee).map(Number),
					).toFixed(2),
					collection: _.sum(
						temData.map((o: any) => o.collectionFee).map(Number),
					).toFixed(2),
					court: _.sum(temData.map((o: any) => o.courtFee).map(Number)).toFixed(
						2,
					),
					surviellance: _.sum(
						temData.map((o: any) => o.surveillanceFee).map(Number),
					).toFixed(2),
					balance: _.sum(
						temData.map((o: any) => o.balance).map(Number),
					).toFixed(2),
				});
				setSummaryData(summaryTem);
				setCreditorTableData(temData);
			} catch (error) {
				appInsights.trackException(
					`Debtor Information General Data view data preparation error - ${error}`,
				);
				setSummaryData(summaryTem);
				setCreditorTableData(temData);
			}
		}
	}, [debtorInformation, creditorInfo, metaData]);

	/**
	 * change the view to postal code selection
	 */
	const changeView = (rest: any) => {
		rest.setFieldValue("postalAreaVisible", true);
	};
	const postalAreaClose = (rest: any) => {
		rest.setFieldValue("postalAreaVisible", false);
	};

	const onChangeCountry = (value: any, rest: any, values: any) => {
		rest.setValues({
			...values,
			selectedCountryCode: value,
			popConfirmVisible: true,
		});
	};

	const saveData = (data: any) => {
		try {
			const {
				debtorType,
				birthDate,
				companyNo,
				personNo,
				addrUnknown,
				email,
				home,
				sms,
			} = data;
			if (debtorType === "N") {
				data.birthDate = null;
				data.idNo = companyNo?.trim()?.replace(/ /g, "");
			} else {
				data.birthDate = moment(birthDate).isValid()
					? moment(birthDate).format(DateFormats.REQ)
					: null;
				data.idNo = personNo?.trim()?.replace(/ /g, "");
			}
			data.pid = "";
			data.phoneNumbers = getPhoneNumbers(data);
			data.isAddressknown = !addrUnknown;
			data.email = email?.trim();
			(data.telephone = home?.trim()), (data.sms = sms?.trim()), save(data);
		} catch (error) {
			appInsights.trackException(
				`Save debtor information data preparation function failed.`,
			);
		}
	};
	const handleSubmit = (data: any) => {
		if (data?.zipCode.length !== 0) {
			if (data?.addrUnknown) {
				setAddressUnknownConfirmVisible(true);
				setFIlledData(data);
			} else {
				setAddressUnknownConfirmVisible(false);
				saveData(data);
			}
		} else {
			saveData(data);
		}
	};

	const handlePostalDoubleClick = (
		selectedPostalAreaDetail: any,
		rest: any,
		values: any,
	) => {
		const { MunicipalityCode, MunicipalityName, PostalCode, City } =
			selectedPostalAreaDetail;
		rest.setValues({
			...values,
			municipalityCode: MunicipalityCode,
			municipalityName: MunicipalityName,
			zipCode: PostalCode,
			zipName: City,
			postalAreaVisible: false,
		});
	};
	const handleAddressUnknown = (checked: boolean, rest: any, values: any) => {
		if (checked) {
			rest.setValues({
				...values,
				addrUnknown: true,
				zipCode: "",
				zipName: "",
				municipalityCode: "",
				municipalityName: "",
			});
		}
	};

	// Credit Information Table Columns and Data
	const columns: any = [
		{
			title: t("US.COLLECTION.DEBTOR:GENERAL.CREDITOR"),
			dataIndex: "Creditor",
			key: "Creditor",
			render: (text: any, record: any) => {
				const { pid, creditorName } = record;
				return (
					<div>
						{pid}
						{pid && " - "}
						{creditorName}
					</div>
				);
			},
		},
		{
			title: t("US.COLLECTION.DEBTOR:GENERAL.NO_OF_CASES"),
			dataIndex: "numberOfCases",
			key: "numberOfCases",
		},
		{
			title: t("US.COLLECTION.DEBTOR:GENERAL.BALANCE"),
			dataIndex: "balance",
			key: "balance",
			align: "right",
			render: (text: any, record: any) => {
				const { balance } = record;
				return (
					<span>
						{balance != 0 && <$AmountLabel value={Number(balance)} />}
						{balance == 0 && "-"}
					</span>
				);
			},
		},
	];

	const AddressHistoryHandler = () => {
		setAddressHistoryVisible(true);
	};
	const AddressHistoryClose = () => {
		setAddressHistoryVisible(false);
	};
	const DebtorHistoryHandler = () => {
		setDebtorHistoryVisible(true);
	};
	const DebtorHistoryClose = () => {
		setDebtorHistoryVisible(false);
	};

	const handlePanel = (keys: any) => {
		setExpandKey(keys);
		if (keys?.includes("3") && !expandKey?.includes("3")) {
			getCreditorInfo(arId);
		}
	};

	const handleBirthday = (values: any, rest: any) => {
		const personNo = values.target.value;
		const data = validatePersonNo(personNo.replaceAll(/\D/g, ""));
		if (data.isValid) {
			formatPersonNo(personNo, rest);
			rest.setFieldValue("birthDate", moment(data.birthDate));
		} else {
			rest.setFieldValue("birthDate", "");
		}
	};

	const handlePersonNo = (data: any, rest: any) => {
		rest.setFieldValue("birthDate", moment(data));
	};

	const countryPopOverConfirmYes = (rest: any, values: any) => {
		rest.setValues({
			...values,
			addrUnknown: true,
			countryCode: values.selectedCountryCode,
			zipCode: "",
			zipName: "",
			municipalityCode: "",
			municipalityName: "",
			popConfirmVisible: false,
		});
	};

	const countryPopOverConfirmNo = (rest: any) => {
		rest.setFieldValue("popConfirmVisible", false);
	};

	const postalCodePopOverConfirmYes = (rest: any) => {
		setAddressUnknownConfirmVisible(false);
		rest.setFieldValue("addrUnknown", false);
		saveData({ ...filledData, addrUnknown: false });
	};
	const postalCodePopOverConfirmNo = (rest: any) => {
		setAddressUnknownConfirmVisible(false);
		rest.setFieldValue("zipCode", "");
		rest.setFieldValue("zipName", "");
		rest.setFieldValue("municipalityCode", "");
		rest.setFieldValue("municipalityName", "");
		saveData({
			...filledData,
			addrUnknown: true,
			zipCode: "",
			zipName: "",
			municipalityCode: "",
			municipalityName: "",
		});
	};

	const onchangeType = (value: any, rest: any) => {
		rest.setFieldValue("debtorType", value);
	};

	const formatOrganizationNo = (data: any, rest: any) => {
		const value = data.target.value;
		if (value?.length === 9) {
			const parts = value.replace(/\s/g, "").match(/.{1,3}/g);
			rest.setFieldValue("companyNo", parts.join(" "));
		}
	};
	const formatPersonNo = (value: any, rest: any) => {
		if (value?.length === 6 || (value?.length >= 9 && value?.length <= 11)) {
			const parts = value.replace(/\s/g, "").match(/.{1,6}/g);
			rest.setFieldValue("personNo", parts.join(" "));
		}
	};

	const searchMunicipilityDetail = (
		countyCode: string,
		values: any,
		rest: any,
	) => {
		if (countyCode?.length > 0) {
			if (searchMunicipility) {
				searchMunicipility({
					countryCode: values?.countryCode ?? DEFAULT_COUNTRY_CODE,
					searchText: countyCode,
					formData: values,
				});
			}
		} else {
			rest.setFieldValue("municipalityName", "");
		}
	};

	return (
		<Formik
			enableReinitialize
			initialValues={formInitialValue(debtorInformation)}
			validationSchema={ValidationSchema}
			validateOnChange 
			validateOnBlur
			onSubmit={handleSubmit}
		>
			{({
				values,
				handleChange,
				handleBlur,
				handleSubmit,
				isSubmitting,
				isValidating,

				...rest
			}: any) => (
				<div>
					<$Form name="GeneralInfo" onSubmit={handleSubmit}>
						<div className="mb-3">
							<$Button
								size="small"
								type="primary"
								disabled={
									values.popConfirmVisible ||
									isSaving ||
									(metaData.data &&
										isSubCaseTransferredToCase(metaData.data)) ||
									!rest.isValid
								}
								onClick={handleSubmit}
								className="px-3"
								data-testid="debtor-general-save"
							>
								{t("US.COLLECTION.COMMON:COMMON.SAVE")}
							</$Button>

							<$Divider className="bui-devider" type="vertical" />
							<$Button
								type="dashed"
								size="small"
								className="px-3 mr-2"
								onClick={() => AddressHistoryHandler()}
								data-testid="debtor-general-addressHistory"
							>
								{t("US.COLLECTION.DEBTOR:INFO.ADDRESSHISTORY")}
							</$Button>
							<$Button
								type="dashed"
								size="small"
								className="px-3 mr-2"
								onClick={() => DebtorHistoryHandler()}
								data-testid="debtor-general-debtorHistory"
							>
								{t("US.COLLECTION.DEBTOR:INFO.DEBTOR_HISTORY")}
							</$Button>
						</div>

						<div className="debtor-info">
							<$Collapse
								bordered={false}
								defaultActiveKey={["1", "2"]}
								expandIcon={({ isActive }) =>
									isActive ? <MinusOutlined /> : <PlusOutlined />
								}
								className="bui-collapse"
								onChange={(panel: any) => handlePanel(panel)}
							>
								{/* Basic information */}
								<$Collapse.Panel
									header={t("US.COLLECTION.DEBTOR:INFO.BASICINFO")}
									key="1"
									data-testid="debtor-general-debtorBasicInfromation"
								>
									<BasicInfo
										values={values}
										rest={rest}
										isFetching={isFetching}
										isSaving={isSaving}
										currentDateFormat={currentDateFormat}
										handleBirthday={handleBirthday}
										handlePersonNo={handlePersonNo}
										formatOrganizationNo={formatOrganizationNo}
										handleAddressUnknown={handleAddressUnknown}
										addressUnknownConfirmVisible={addressUnknownConfirmVisible}
										postalCodePopOverConfirmYes={postalCodePopOverConfirmYes}
										postalCodePopOverConfirmNo={postalCodePopOverConfirmNo}
										onchangeType={onchangeType}
									/>
								</$Collapse.Panel>

								{/* Address information */}
								<$Collapse.Panel
									header={t("US.COLLECTION.DEBTOR:INFO.ADDRESSINFO")}
									key="2"
									data-testid="debtor-general-addressInformation"
								>
									<AddressInfo
										values={values}
										rest={rest}
										isFetching={isFetching}
										isSaving={isSaving}
										postalcode={postalcode}
										changeView={changeView}
										searchPostalcode={searchPostalcode}
										searchMunicipilityDetail={searchMunicipilityDetail}
										onChangeCountry={onChangeCountry}
										countryPopOverConfirmYes={countryPopOverConfirmYes}
										countryPopOverConfirmNo={countryPopOverConfirmNo}
									/>
								</$Collapse.Panel>

								{/* Creditor information */}
								<$Collapse.Panel
									header={t("US.COLLECTION.DEBTOR:GENERAL.CREDITORCASES")}
									key="3"
									data-testid="debtor-general-creditorInformation"
								>
									<CreditorInfo
										isFetchingCreditorInfo={isFetchingCreditorInfo}
										creditorTableData={creditorTableData}
										summaryData={summaryData}
										columns={columns}
									/>
								</$Collapse.Panel>

								{/* Other information */}
								<$Collapse.Panel
									header={t("US.COLLECTION.DEBTOR:GENERAL.OTHER_INFORMATION")}
									key="4"
									collapsible={
										moment
											.utc(otherInfo?.lastLoginPortal)
											.isSame(moment.utc(DateFormats.MIN_DATE)) &&
											otherInfo?.payment == 0
											? "disabled"
											: undefined
									}
									data-testid="debtor-general-otherInformation"
								>
									<OtherInfo
										isFetchingOtherInformation={isFetchingOtherInformation}
										otherInfo={otherInfo}
									/>
								</$Collapse.Panel>
							</$Collapse>
						</div>

						{/* Previous Code */}
						<div className="mt-2 mb-5"></div>
					</$Form>
					<$Drawer
						title={t("US.COLLECTION.DEBTOR:INFO.ADDRESSHISTORY")}
						width={"90%"}
						visible={AddressHistoryVisible}
						onClose={AddressHistoryClose}
						destroyOnClose
					>
						<AddressHistory onClose={AddressHistoryClose} />
					</$Drawer>
					<$Drawer
						title={t("US.COLLECTION.DEBTOR:INFO.DEBTOR_HISTORY")}
						width={1200}
						visible={DebtorHistoryVisible}
						onClose={DebtorHistoryClose}
						destroyOnClose
					>
						<EntityHistory onClose={AddressHistoryClose} {...props} />
					</$Drawer>
					<$Drawer
						title={drawerName}
						width={800}
						visible={values.postalAreaVisible}
						onClose={() => postalAreaClose(rest)}
						destroyOnClose
					>
						<PostalArea
							postalAreaClose={() => postalAreaClose(rest)}
							drawerContentChangeClick={(e: any) => {
								handlePostalDoubleClick(e?.updatedInfo, rest, values)
							}
							}
							countryCode={values.countryCode}
							{...props}
						/>
					</$Drawer>
				</div>
			)}
		</Formik>
	);
};

const mapStateToProps = (state: IRootState) => {
	const { common, domainView, entityDetails, debtorDetail } = state;
	const { currentCurrency, currentLanguage, currentDateFormat } = common;
	const { creditor, other, isSaving } = debtorDetail;
	const { metaData } = domainView;
	return {
		currentCurrency,
		currentLanguage,
		currentDateFormat,
		isFetching: entityDetails.isFetching,
		postalAreas: entityDetails.postalAreas,
		debtorInformation: entityDetails.entity,
		isFetchingCreditorInfo: creditor.isFetching,
		creditorInfo: creditor.data,
		otherInfo: other.data,
		isFetchingOtherInformation: other.isFetching,
		drawerName: entityDetails.drawerName,
		isSaving,
		metaData,
	};
};

const mapDispatchToProps = {
	getCreditorInfo: debtor.creditor.getByArNo,
	getOtherInfo: debtor.other.getByArNo,
	save: debtor.debtor.save,
	getEntity: entity.get,
	searchPostalcode: debtor.postalCode.search,
	searchMunicipility: debtor.minicipality.search,
	resetPostalDetails: debtor.postalCode.reset,
};

const wrappedForm = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(General),
);
export default wrappedForm;
